import { lazy, ComponentType } from 'react'

export const RELOADED_FLAG = 'lazy-chunk-reloaded'
const RELOAD_GUARD_TTL_MS = 5 * 60 * 1000
const RELOAD_FALLBACK_MS = 4 * 1000

function hasRecentReloadAttempt(): boolean {
  const raw = sessionStorage.getItem(RELOADED_FLAG)
  if (!raw) return false

  const attemptedAt = Number(raw)
  const isRecent = Number.isFinite(attemptedAt) &&
    attemptedAt > 0 &&
    Date.now() - attemptedAt < RELOAD_GUARD_TTL_MS

  // 舊版存的是字串 "1"；過期或無效的 guard 不可以永久阻止日後自救。
  if (!isRecent) sessionStorage.removeItem(RELOADED_FLAG)
  return isRecent
}

// chunk 載入失敗（多半是改版部署後，舊 index.html 引用已刪除的舊 chunk）→
// 整頁重載一次拿新版 index。sessionStorage 的短效 flag 防止「重載後還是失敗 →
// 無限重載」；任一 chunk 載入成功就清掉 flag，下次部署還能再自救一次。
//
// iOS standalone PWA 偶爾不會完成 location.reload()。因此不能回傳永久 pending
// promise；4 秒後仍留在原頁，就把錯誤交給 ErrorBoundary 顯示可操作的恢復畫面。
// 抽成獨立 factory 讓串接邏輯可被單元測試直接斷言。
export function createRetryImport<T>(
  importFn: () => Promise<T>
): () => Promise<T> {
  return () =>
    importFn()
      .then(module => {
        sessionStorage.removeItem(RELOADED_FLAG)
        return module
      })
      // ⛔ 失敗先「重試一次」再考慮整頁重載。
      // 使用者快速切換頁面時，上一頁還在下載的 chunk 會被瀏覽器取消，
      // 這跟「chunk 真的不存在（部署換版）」是兩回事，但拋出的錯誤長得一樣。
      // 直接 reload 會形成惡性循環：reload → 有 chunk 載入成功 → 上面的 then
      // 清掉 guard flag → 使用者再切換 → 又被取消 → 又 reload…
      // 使用者看到的就是「一直在載入中」。重試一次即可分辨：被取消的重試會成功，
      // 真的不存在的仍會失敗，再走原本的自救流程。
      .catch((firstErr: unknown) => {
        return importFn()
          .then(module => {
            sessionStorage.removeItem(RELOADED_FLAG)
            return module
          })
          .catch(() => {
            throw firstErr
          })
      })
      .catch((err: unknown) => {
        if (!hasRecentReloadAttempt()) {
          sessionStorage.setItem(RELOADED_FLAG, String(Date.now()))

          return new Promise<T>((_, reject) => {
            // 先安排 fallback，再觸發 reload；即使 WebKit 忽略 reload，也不會永遠轉圈。
            setTimeout(() => reject(err), RELOAD_FALLBACK_MS)
            try {
              window.location.reload()
            } catch {
              reject(err)
            }
          })
        }
        // 重載過一次仍失敗 → 真的有問題，拋給 ErrorBoundary
        throw err
      })
}

// ComponentType<any>：對齊 React.lazy 自身的型別簽名（unknown 會跟 FC<{}> 不相容）
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function lazyWithReload<T extends ComponentType<any>>(
  importFn: () => Promise<{ default: T }>
) {
  return lazy(createRetryImport(importFn))
}
